/**
 * Drizzle query builders + query keys de TUTORÍAS (M3).
 *
 * Consumo local-first: `submission_version` viaja por my_cohorts_student (mías) y
 * my_cohorts_teacher / org_admin_scope (las de mis cohortes = cola de corrección);
 * `tutoring_settings` + `assignment_review_policy` por academy_org (gating de UI).
 */

import {
  and,
  asc,
  assignment_review_policy,
  desc,
  eq,
  isNull,
  type PowerSyncDrizzleDB,
  submission_version,
  tutoring_settings,
} from "@space/powersync";

export const tutoringQueryKeys = {
  all: ["academy-tutoring"] as const,
  versions: (
    assignmentId: string | undefined,
    cohortId: string | undefined,
    userId: string | undefined,
  ) => ["academy-tutoring", "versions", assignmentId, cohortId, userId] as const,
  queue: (cohortId: string | undefined) => ["academy-tutoring", "queue", cohortId] as const,
  queueAll: ["academy-tutoring", "queue-all"] as const,
  settings: (orgId: string | undefined) => ["academy-tutoring", "settings", orgId] as const,
  reviewPolicy: (assignmentId: string | undefined) =>
    ["academy-tutoring", "review-policy", assignmentId] as const,
  assignmentsIndex: ["academy-tutoring", "assignments-index"] as const,
  usersIndex: ["academy-tutoring", "users-index"] as const,
};

/** Historial de versiones de una entrega (asc por versión). */
export function getSubmissionVersionsQuery(
  db: PowerSyncDrizzleDB,
  assignmentId: string,
  cohortId: string,
  userId: string,
) {
  return db
    .select()
    .from(submission_version)
    .where(
      and(
        eq(submission_version.assignment_id, assignmentId),
        eq(submission_version.cohort_id, cohortId),
        eq(submission_version.user_id, userId),
      ),
    )
    .orderBy(asc(submission_version.version));
}

/**
 * Cola de corrección: versiones PENDIENTES (outcome IS NULL) de las cohortes que
 * el actor ve (los streams ya acotan a sus cohortes). Orden por antigüedad para
 * el cálculo de SLA en la UI. Sin `cohortId` = todas las cohortes visibles.
 */
export function getReviewQueueQuery(db: PowerSyncDrizzleDB, cohortId?: string) {
  const pending = isNull(submission_version.outcome);
  return db
    .select()
    .from(submission_version)
    .where(cohortId ? and(pending, eq(submission_version.cohort_id, cohortId)) : pending)
    .orderBy(asc(submission_version.submitted_at));
}

/** Versión pendiente MÁS RECIENTE de una entrega (para revisar). */
export function getLatestPendingVersionQuery(
  db: PowerSyncDrizzleDB,
  assignmentId: string,
  cohortId: string,
  userId: string,
) {
  return db
    .select()
    .from(submission_version)
    .where(
      and(
        eq(submission_version.assignment_id, assignmentId),
        eq(submission_version.cohort_id, cohortId),
        eq(submission_version.user_id, userId),
      ),
    )
    .orderBy(desc(submission_version.version))
    .limit(1);
}

/** Config de tutorías por org (una fila; puede no existir → usar fallback). */
export function getTutoringSettingsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db.select().from(tutoring_settings).where(eq(tutoring_settings.organization_id, orgId));
}

/** Override de política por tarea (puede no existir → hereda de la org). */
export function getReviewPolicyQuery(db: PowerSyncDrizzleDB, assignmentId: string) {
  return db
    .select()
    .from(assignment_review_policy)
    .where(eq(assignment_review_policy.assignment_id, assignmentId));
}
